import { create } from "zustand";
import type { AdsWorkspaceState, SubAgentId } from "./useAdsWorkspaceStore.types";
import {
  hydrateActiveSubAgent,
  hydrateGeneratedAds,
  hydratePendingJobs,
  hydrateRevealedSubAgents,
  hydrateSelectedBrandId,
  hydrateSubAgentSessionId,
  persistGeneratedAds,
  persistPendingJobs,
  persistSelectedBrandId,
  persistSubAgentState,
  saveWorkspaceCompanionState,
  setFlatStateWorkspace,
} from "./adsWorkspacePersistence";
import { createWorkspaceScopeSlice } from "./slices/workspaceScopeSlice";
import { createGeneratedAdsSlice } from "./slices/generatedAdsSlice";
import { createJobsSlice } from "./slices/jobsSlice";
import { createSubAgentChatSlice } from "./slices/subAgentChatSlice";
import { createPendingHandoffSlice } from "./slices/pendingHandoffSlice";
import { createCompanionChatUiSlice } from "./slices/companionChatUiSlice";

// Type definitions now live in ./useAdsWorkspaceStore.types — re-exported here
// so existing `import { … } from ".../useAdsWorkspaceStore"` sites keep working.
export type {
  AdAssetKind,
  AdPlatformConfig,
  AdsCompanionSessionControls,
  AdsPendingConfirmation,
  AdsPlatform,
  AdsProductSummary,
  AdsSessionSummary,
  AdsWorkspaceState,
  AdType,
  BananBatchItemLineage,
  HiggsfieldBatchItemLineage,
  KlingBatchItemLineage,
  MarcusGuidedAdsFlowId,
  PendingBananJob,
  PendingChatImageAttachment,
  PendingHiggsfieldJob,
  PendingKlingJob,
  PendingMarcusGuidedFlow,
  PendingScoutResearchRun,
  SerializedUIMessage,
  SubAgentChatState,
  SubAgentId,
  SubAgentSessionSummary,
} from "./useAdsWorkspaceStore.types";

const _hydratedJobs = hydratePendingJobs();

export const useAdsWorkspaceStore = create<AdsWorkspaceState>()((set, get) => ({
  scopeWorkspaceId: null,
  selectedBrandId: hydrateSelectedBrandId(),
  selectedBrandConfig: null,
  products: [],
  selectedProductIds: [],
  activeTab: "create",
  selectedPlatform: null,
  pendingConfirmation: null,
  updatedAt: null,
  generatedAds: hydrateGeneratedAds(),
  performanceForecast: null,
  performanceForecastProductId: null,
  performanceForecasting: false,
  lastImageAdForm: null,
  companionSessionControls: null,
  pendingKlingJobs: _hydratedJobs.pendingKlingJobs,
  pendingBananJobs: _hydratedJobs.pendingBananJobs,
  pendingHiggsfieldJobs: [],
  selectedAssetIds: [],
  favoriteAssetIds: [],

  adPlatformConfig: {
    google: { connected: false },
    meta: { connected: false },
  },

  activeSubAgent: hydrateActiveSubAgent(),
  revealedSubAgents: hydrateRevealedSubAgents(),
  subAgentChats: {
    banan: {
      sessionId: hydrateSubAgentSessionId("banan"),
      sessionCreatedAt: null,
      cachedMessages: [],
    },
    vid: { sessionId: hydrateSubAgentSessionId("vid"), sessionCreatedAt: null, cachedMessages: [] },
    scout: { sessionId: null, sessionCreatedAt: null, cachedMessages: [] },
    gads: {
      sessionId: hydrateSubAgentSessionId("gads"),
      sessionCreatedAt: null,
      cachedMessages: [],
    },
    // Migrate the legacy `ads_cli` key (where Mads v1/v2 session ids were
    // persisted before the slot rename) into the new `meta_ads` slot when
    // the new key is empty.
    meta_ads: {
      sessionId:
        hydrateSubAgentSessionId("meta_ads") ?? hydrateSubAgentSessionId("ads_cli" as SubAgentId),
      sessionCreatedAt: null,
      cachedMessages: [],
    },
    higgsfield: {
      sessionId: hydrateSubAgentSessionId("higgsfield"),
      sessionCreatedAt: null,
      cachedMessages: [],
    },
    higgsfield_cli: {
      sessionId: hydrateSubAgentSessionId("higgsfield_cli"),
      sessionCreatedAt: null,
      cachedMessages: [],
    },
    agent_v: {
      sessionId: hydrateSubAgentSessionId("agent_v"),
      sessionCreatedAt: null,
      cachedMessages: [],
    },
    moodboard: {
      sessionId: hydrateSubAgentSessionId("moodboard"),
      sessionCreatedAt: null,
      cachedMessages: [],
    },
  },
  subAgentSessionList: {
    banan: [],
    vid: [],
    scout: [],
    gads: [],
    meta_ads: [],
    higgsfield: [],
    higgsfield_cli: [],
    agent_v: [],
    moodboard: [],
  },
  subAgentBusy: {
    banan: false,
    vid: false,
    scout: false,
    gads: false,
    meta_ads: false,
    higgsfield: false,
    higgsfield_cli: false,
    agent_v: false,
    moodboard: false,
  },

  pendingCompanionPrompt: null,

  pendingChatImageAttachment: null,

  pendingSubAgentInput: null,
  pendingMarcusGuidedFlow: null,

  pendingScoutResearchRun: null,

  pendingMetaTasksOutcome: null,
  pendingGadsTasksOutcome: null,

  pendingElementPlanReviewOpen: false,

  // Telegram-style chat UI
  companionViewLevel: "list",
  selectedChatAgent: null,
  unreadCounts: {
    marcus: 0,
    banan: 0,
    vid: 0,
    scout: 0,
    gads: 0,
    meta_ads: 0,
    higgsfield: 0,
    higgsfield_cli: 0,
    agent_v: 0,
    moodboard: 0,
  },
  lastMessagePreviews: {
    marcus: null,
    banan: null,
    vid: null,
    scout: null,
    gads: null,
    meta_ads: null,
    higgsfield: null,
    higgsfield_cli: null,
    agent_v: null,
    moodboard: null,
  },

  ...createWorkspaceScopeSlice(set, get),
  ...createGeneratedAdsSlice(set, get),
  ...createJobsSlice(set, get),
  ...createSubAgentChatSlice(set, get),
  ...createPendingHandoffSlice(set, get),
  ...createCompanionChatUiSlice(set, get),
}));

// Sync ephemeral state to sessionStorage on every change
useAdsWorkspaceStore.subscribe((state, prev) => {
  const subAgentChanged =
    state.revealedSubAgents !== prev.revealedSubAgents ||
    state.subAgentChats !== prev.subAgentChats ||
    state.activeSubAgent !== prev.activeSubAgent;
  const jobsChanged =
    state.pendingBananJobs !== prev.pendingBananJobs ||
    state.pendingKlingJobs !== prev.pendingKlingJobs;
  const adsChanged = state.generatedAds !== prev.generatedAds;
  const brandChanged = state.selectedBrandId !== prev.selectedBrandId;

  if (subAgentChanged) {
    persistSubAgentState(state.revealedSubAgents, state.subAgentChats, state.activeSubAgent);
  }
  if (jobsChanged) {
    persistPendingJobs(state.pendingBananJobs, state.pendingKlingJobs);
  }
  if (adsChanged) {
    persistGeneratedAds(state.generatedAds);
  }
  if (brandChanged) {
    persistSelectedBrandId(state.selectedBrandId);
  }

  // Keep the workspace tag in sync with whichever workspace is currently in
  // scope so a page refresh hydrates the flat keys for the right workspace
  // (and so cross-workspace state inherited at module load stays quarantined).
  if ((subAgentChanged || jobsChanged || adsChanged || brandChanged) && state.scopeWorkspaceId) {
    setFlatStateWorkspace(state.scopeWorkspaceId);
  }

  // Keep the per-workspace snapshot fresh so a page refresh can round-trip
  // revealed sub-agents, pending jobs, generated ads, and brand selection
  // via restoreWorkspaceCompanionState (not just via the flat sessionStorage
  // keys which can be wiped by setScopeWorkspace).
  if (state.scopeWorkspaceId && (subAgentChanged || jobsChanged || adsChanged || brandChanged)) {
    saveWorkspaceCompanionState(state.scopeWorkspaceId, state);
  }
});
